import { getMadridDateKeyFromPickerDate, getMadridDateKeyFromTimestamp, getMadridDateKeyNow } from '@firemenu/dates';
import { addDays, addHours, addMinutes, addMonths, endOfWeek, format, parse, startOfWeek } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

const MADRID_TZ = 'Europe/Madrid';

/** Wall-clock in Europe/Madrid from a UTC millisecond timestamp. */
export function formatMadridWallTime(ms: number, pattern: string): string {
  return formatInTimeZone(ms, MADRID_TZ, pattern);
}

export { getMadridDateKeyFromPickerDate, getMadridDateKeyFromTimestamp, getMadridDateKeyNow };

/** Inclusive range of Madrid calendar `dateKey`s (matches Cloud Functions `buildDateKeysRange`). */
export function buildMadridDateKeysRange(fromDateKey: number, toDateKey: number): number[] {
  if (fromDateKey > toDateKey) {
    return [];
  }
  const result: number[] = [];
  let current = fromDateKey;
  while (current <= toDateKey) {
    result.push(current);
    const d = parse(String(current), 'yyyyMMdd', new Date());
    current = Number(format(addDays(d, 1), 'yyyyMMdd'));
  }
  return result;
}

/** UTC millisecond range [start, end] for that Madrid calendar day (aligned with `dateKeyToMadridTimeRange` in functions). */
export function getMadridDayTimeRangeForDateKey(dateKey: number): [number, number] {
  const parsedCalendarDay = parse(String(dateKey), 'yyyyMMdd', new Date());
  const dayStr = format(parsedCalendarDay, 'yyyy-MM-dd');
  const start = fromZonedTime(parse(`${dayStr} 00:00:00.000`, 'yyyy-MM-dd HH:mm:ss.SSS', new Date()), MADRID_TZ);
  const end = fromZonedTime(parse(`${dayStr} 23:59:59.999`, 'yyyy-MM-dd HH:mm:ss.SSS', new Date()), MADRID_TZ);
  return [start.getTime(), end.getTime()];
}

/** Madrid «сьогодні» paid-time bounds (same as archive day window). */
export function getMadridDayTimeRangeNow(nowMs: number = Date.now()): [number, number] {
  return getMadridDayTimeRangeForDateKey(getMadridDateKeyNow(nowMs));
}

/** Початок мадридської календарної доби для мітки часу (для групування в чартах). */
export function getMadridDayStartMsFromTimestamp(ms: number): number {
  return getMadridDayTimeRangeForDateKey(getMadridDateKeyFromTimestamp(ms))[0];
}

/** Ключ години за годинником у Мадриді (групування в чартах). */
export function getMadridHourBucketKey(ms: number): string {
  return format(toZonedTime(new Date(ms), MADRID_TZ), 'yyyyMMddHH');
}

/** Вчора 00:00:00.000 — сьогодні 23:59:59.999 у Мадриді. */
export function getMadrid2DaysTimeRange(nowMs: number = Date.now()): [number, number] {
  const todayKey = getMadridDateKeyNow(nowMs);
  const d = parse(String(todayKey), 'yyyyMMdd', new Date());
  const yesterdayKey = Number(format(addDays(d, -1), 'yyyyMMdd'));
  return [getMadridDayTimeRangeForDateKey(yesterdayKey)[0], getMadridDayTimeRangeForDateKey(todayKey)[1]];
}

/** Поточний календарний місяць у Мадриді (повні межі доб першого/останнього дня). */
export function getMadridMonthTimeRange(nowMs: number = Date.now()): [number, number] {
  const madrid = toZonedTime(new Date(nowMs), MADRID_TZ);
  const y = Number(format(madrid, 'yyyy'));
  const mo = Number(format(madrid, 'MM'));
  const firstKey = y * 10000 + mo * 100 + 1;
  const lastDay = new Date(y, mo, 0).getDate();
  const lastKey = y * 10000 + mo * 100 + lastDay;
  return [getMadridDayTimeRangeForDateKey(firstKey)[0], getMadridDayTimeRangeForDateKey(lastKey)[1]];
}

/** Поточний тиждень (пн–нд) у Мадриді. */
export function getMadridWeekTimeRange(nowMs: number = Date.now()): [number, number] {
  const madrid = toZonedTime(new Date(nowMs), MADRID_TZ);
  const weekStart = startOfWeek(madrid, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(madrid, { weekStartsOn: 1 });
  const startKey = Number(format(weekStart, 'yyyyMMdd'));
  const endKey = Number(format(weekEnd, 'yyyyMMdd'));
  return [getMadridDayTimeRangeForDateKey(startKey)[0], getMadridDayTimeRangeForDateKey(endKey)[1]];
}

/** Попередній календарний тиждень (пн–нд) у Мадриді. */
export function getMadridPreviousWeekTimeRange(nowMs: number = Date.now()): [number, number] {
  const [thisWeekStart] = getMadridWeekTimeRange(nowMs);
  return getMadridWeekTimeRange(thisWeekStart - 1);
}

/**
 * Початок мадридської календарної доби після зсуву на час початку зміни.
 * `shiftStartReferenceMs` — довільна мітка з потрібними год:хв у Мадриді (як `cafe.startShiftTime`).
 */
function getMadridShiftDayStartMs(logMs: number, shiftStartReferenceMs: number): number {
  const shiftWall = toZonedTime(new Date(shiftStartReferenceMs || 0), MADRID_TZ);
  const shiftH = shiftWall.getHours();
  const shiftM = shiftWall.getMinutes();
  const shifted = addMinutes(addHours(new Date(logMs), -shiftH), -shiftM);
  return getMadridDayStartMsFromTimestamp(shifted.getTime());
}

/** ~2 тижні від понеділка тижня, що містить (зараз − 7 днів) у Мадриді (як попередній `get2WeeksTimeRange`). */
export function getMadrid2WeeksTimeRange(nowMs: number = Date.now()): [number, number] {
  const madrid = toZonedTime(new Date(nowMs), MADRID_TZ);
  const anchor = addDays(madrid, -7);
  const weekStart = startOfWeek(anchor, { weekStartsOn: 1 });
  const startKey = Number(format(weekStart, 'yyyyMMdd'));
  const endDay = addDays(parse(String(startKey), 'yyyyMMdd', new Date()), 14);
  const endKey = Number(format(endDay, 'yyyyMMdd'));
  return [getMadridDayTimeRangeForDateKey(startKey)[0], getMadridDayTimeRangeForDateKey(endKey)[1]];
}

/** Значення для MUI DatePicker: полудень у Мадриді (стабільний календарний день для `dateKey`). */
export function getMadridPickerDateForDateKey(dateKey: number): Date {
  const dayStr = format(parse(String(dateKey), 'yyyyMMdd', new Date()), 'yyyy-MM-dd');
  return fromZonedTime(parse(`${dayStr} 12:00:00.000`, 'yyyy-MM-dd HH:mm:ss.SSS', new Date()), MADRID_TZ);
}

/** ISO-тиждень (пн–нд) у Мадриді: зсув на ±N тижнів від поточного діапазону. */
export function shiftMadridIsoWeekRange(range: [number, number], weekDelta: number): [number, number] {
  const mondayKey = getMadridDateKeyFromTimestamp(range[0]);
  const mondayParsed = parse(String(mondayKey), 'yyyyMMdd', new Date());
  const targetMonday = addDays(mondayParsed, weekDelta * 7);
  const targetMondayKey = Number(format(targetMonday, 'yyyyMMdd'));
  const targetSundayKey = Number(format(addDays(targetMonday, 6), 'yyyyMMdd'));
  return [getMadridDayTimeRangeForDateKey(targetMondayKey)[0], getMadridDayTimeRangeForDateKey(targetSundayKey)[1]];
}

/** Повний календарний місяць у Мадриді: зсув на ±N місяців від місяця, що містить початок `range`. */
export function shiftMadridMonthRange(range: [number, number], monthDelta: number): [number, number] {
  const ymd = getMadridDateKeyFromTimestamp(range[0]);
  const d = parse(String(ymd), 'yyyyMMdd', new Date());
  const shifted = addMonths(d, monthDelta);
  const y = Number(format(shifted, 'yyyy'));
  const mo = Number(format(shifted, 'MM'));
  const firstKey = y * 10000 + mo * 100 + 1;
  const lastDay = new Date(y, mo, 0).getDate();
  const lastKey = y * 10000 + mo * 100 + lastDay;
  return [getMadridDayTimeRangeForDateKey(firstKey)[0], getMadridDayTimeRangeForDateKey(lastKey)[1]];
}

/** Попередній мадридський календарний день (dateKey). */
export function getMadridYesterdayDateKey(nowMs: number = Date.now()): number {
  const todayKey = getMadridDateKeyNow(nowMs);
  const d = parse(String(todayKey), 'yyyyMMdd', new Date());
  return Number(format(addDays(d, -1), 'yyyyMMdd'));
}

/**
 * Індекс дня для `promotion.days` / `p.days`: `'0'` = пн … `'6'` = нд
 * (той самий мапінг, що раніше робили через `getDay()` у локалі).
 */
export function getMadridPromotionDayIndexMon0(nowMs: number = Date.now()): string {
  const dk = getMadridDateKeyFromTimestamp(nowMs);
  const str = String(dk);
  const y = Number(str.slice(0, 4));
  const mo = Number(str.slice(4, 6));
  const d = Number(str.slice(6, 8));
  const dowSun0 = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0)).getUTCDay();
  return String((dowSun0 + 6) % 7);
}

/** UTC ms: сьогодні за календарем Мадрида + год:хв на годиннику Мадрида. */
export function getMadridTodayWallInstantMs(hours: number, minutes: number, nowMs: number = Date.now()): number {
  const dk = getMadridDateKeyNow(nowMs);
  const dayStr = format(parse(String(dk), 'yyyyMMdd', new Date()), 'yyyy-MM-dd');
  const p = (n: number) => String(n).padStart(2, '0');
  return fromZonedTime(
    parse(`${dayStr} ${p(hours)}:${p(minutes)}:00.000`, 'yyyy-MM-dd HH:mm:ss.SSS', new Date()),
    MADRID_TZ
  ).getTime();
}

// ─── Operational-day helpers ─────────────────────────────────────────────────

/**
 * UTC millisecond [start, end] of the operational day that contains `ms`.
 * The day always spans exactly until the next shift boundary (≈24 h ± DST).
 */
function getMadridOperationalDayTimeRange(ms: number, shiftStartMs: number): [number, number] {
  const startMs = getMadridShiftDayStartMs(ms, shiftStartMs);
  // Find the start of the *next* operational day by probing 25 h ahead
  const endMs = getMadridShiftDayStartMs(startMs + 25 * 3600 * 1000, shiftStartMs) - 1;
  return [startMs, endMs];
}

/** Today's operational day [start, end]. */
function getMadridOperationalDayNowRange(shiftStartMs: number, nowMs: number = Date.now()): [number, number] {
  return getMadridOperationalDayTimeRange(nowMs, shiftStartMs);
}

/**
 * Last N operational days ending today (inclusive).
 * e.g. N=7 → today + previous 6 operational days.
 */
export function getMadridLastNOperationalDaysRange(
  n: number,
  shiftStartMs: number,
  nowMs: number = Date.now()
): [number, number] {
  const [todayStart, todayEnd] = getMadridOperationalDayTimeRange(nowMs, shiftStartMs);
  let rangeStart = todayStart;
  for (let i = 1; i < n; i++) {
    rangeStart = getMadridShiftDayStartMs(rangeStart - 1, shiftStartMs);
  }
  return [rangeStart, todayEnd];
}

/** Operational-day range for a specific calendar month (1-based month). */
export function getMadridOperationalSpecificMonthRange(
  year: number,
  month: number,
  shiftStartMs: number
): [number, number] {
  const firstKey = year * 10000 + month * 100 + 1;
  const lastDay = new Date(year, month, 0).getDate(); // month is 1-based: new Date(y, m, 0) = last day
  const lastKey = year * 10000 + month * 100 + lastDay;
  const [calStart] = getMadridDayTimeRangeForDateKey(firstKey);
  const [, calEnd] = getMadridDayTimeRangeForDateKey(lastKey);
  return [
    getMadridShiftDayStartMs(calStart, shiftStartMs),
    getMadridShiftDayStartMs(calEnd + 25 * 3600 * 1000, shiftStartMs) - 1,
  ];
}

/** Operational-day range for a specific calendar year. Past years: full year. Current/future: Jan 1 – today. */
export function getMadridOperationalSpecificYearRange(
  year: number,
  shiftStartMs: number,
  nowMs: number = Date.now()
): [number, number] {
  const jan1Key = year * 10000 + 101;
  const [jan1Start] = getMadridDayTimeRangeForDateKey(jan1Key);
  const currentYear = Number(format(toZonedTime(new Date(nowMs), MADRID_TZ), 'yyyy'));
  if (year >= currentYear) {
    const todayEnd = getMadridOperationalDayNowRange(shiftStartMs, nowMs)[1];
    return [getMadridShiftDayStartMs(jan1Start, shiftStartMs), todayEnd];
  }
  const dec31Key = year * 10000 + 1231;
  const [, dec31End] = getMadridDayTimeRangeForDateKey(dec31Key);
  return [
    getMadridShiftDayStartMs(jan1Start, shiftStartMs),
    getMadridShiftDayStartMs(dec31End + 25 * 3600 * 1000, shiftStartMs) - 1,
  ];
}

/** Operational-day range for an arbitrary date key range (used by custom mode). */
export function getMadridOperationalCustomRange(
  startDateKey: number,
  endDateKey: number,
  shiftStartMs: number
): [number, number] {
  const [calStart] = getMadridDayTimeRangeForDateKey(startDateKey);
  const [, calEnd] = getMadridDayTimeRangeForDateKey(endDateKey);
  return [
    getMadridShiftDayStartMs(calStart, shiftStartMs),
    getMadridShiftDayStartMs(calEnd + 25 * 3600 * 1000, shiftStartMs) - 1,
  ];
}

/** Comparison range for "same period in the previous calendar month". */
export function getMadridSamePeriodPreviousMonth(timeRange: [number, number]): [number, number] {
  const [prevMonthStart] = shiftMadridMonthRange(timeRange, -1);
  const duration = timeRange[1] - timeRange[0];
  return [prevMonthStart, prevMonthStart + duration];
}

/** Comparison range for "same period one year ago". */
export function getMadridSamePeriodLastYear(timeRange: [number, number]): [number, number] {
  const MS_PER_YEAR = 365.25 * 24 * 3600 * 1000;
  return [timeRange[0] - MS_PER_YEAR, timeRange[1] - MS_PER_YEAR];
}

/** День тижня 0–6 (нд–сб) для мадридської календарної дати з date-picker — як `Date.getDay()`. */
export function getMadridWeekdaySun0FromPickerDate(d: Date | null, nowMs: number = Date.now()): number {
  const dk = getMadridDateKeyFromPickerDate(d, nowMs);
  const str = String(dk);
  const y = Number(str.slice(0, 4));
  const mo = Number(str.slice(4, 6));
  const day = Number(str.slice(6, 8));
  return new Date(Date.UTC(y, mo - 1, day, 12, 0, 0)).getUTCDay();
}
